import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemChip, ActionChip } from '../pills';
import { OptToken } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { ConfirmDialog } from '../ConfirmDialog';
import { TicketDetailPanel, TicketUnitCandidate } from '../TicketDetailPanel';
import { InvoiceDetailPanel } from '../InvoiceDetailPanel';
import { useListFetch } from '../useListFetch';
import { useWriteFeedback } from '../useWriteFeedback';
import { AddLeaseForm } from './AddLeaseForm';

/**
 * UnitDrillInOverlay — the §0.4/AC0.7 unit → lease/tenant/payments/work-orders drill-in.
 *
 * Container: NOT the suite's master-detail static side panel (Accounts/Deals/Tickets) — that shape
 * assumes a dedicated master list the Portfolio page doesn't have. The right precedent is "something
 * clicked from INSIDE another surface" — the ticket-create-from-conversation overlay
 * (agent-console.tsx:~2547): `fixed inset-0 z-50` dim backdrop, panel sliding in anchored to the END
 * edge. That literal instance has no Escape/backdrop-dismiss of its own (closes only via its panel's
 * internal button) — §0.4 asks for Escape + backdrop-click too, so both are added here the same way
 * SheetShell already does it elsewhere (document keydown + outside-pointer check), not invented fresh.
 *
 * Work-order rows open the real TicketDetailPanel in a SECOND overlay of the same shape, rendered as
 * a later sibling in this same tree — at equal z-50, later DOM order paints on top, so closing it
 * returns to this one with no bespoke z-index logic (§0.4 point 5).
 */

type Occupancy = 'occupied' | 'on-notice' | 'vacant';
type RentStatus = 'vacant' | 'unbilled' | 'paid' | 'late' | 'due-soon' | 'open';
type Reachability = 'sendable' | 'window-closed' | 'not-connected';

interface DrillIn {
  unit: { id: string; label: string; status: Occupancy; propertyId: string; propertyName: string };
  tenant: { leadId: string; name: string | null } | null;
  lease: { id: string; startDate: string; endDate: string; rent: number; renewalState: string; endedAt: string | null; noticeGivenAt: string | null } | null;
  payments: Array<{ id: string; dueDate: string | null; amount: number; status: RentStatus }>;
  paymentsTotal: number;
  reminder: { invoiceId: string | null; status: RentStatus; reachability: Reachability | null; remindedAt: string | null } | null;
  workOrders: Array<{ id: string; number: string; subject: string; priority: string | null; resolvedAt: string | null }>;
  // I6/§3 — Documents filed against the SAME selected lease as payments (never all leases this unit
  // has ever had); empty when no lease is selected.
  documents: Array<{ id: string; name: string; mimeType: string; size: number; uploadedAt: string }>;
  // I5/§4/AC5.3 — every Lease on file for this Unit (any state) — the delete pre-offer check.
  leaseCount: number;
}

// I2 §3 — the same "expiring soon" horizon the Leases-expiring card/KPI already use
// (PropertyService.ts EXPIRING_SOON_WINDOW_MS, §4.1), re-expressed client-side so Renew's trigger
// window can never disagree with it.
const EXPIRING_SOON_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const STATUS_TOKEN: Record<Occupancy, OptToken> = { occupied: 'emerald', 'on-notice': 'amber', vacant: 'rose' };
const PAYMENT_TOKEN: Record<'paid' | 'due' | 'late', OptToken> = { paid: 'emerald', due: 'amber', late: 'rose' };
const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();
const fmtDocSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function paymentTone(status: RentStatus): 'paid' | 'due' | 'late' {
  if (status === 'paid') return 'paid';
  if (status === 'late') return 'late';
  return 'due'; // due-soon / open / unbilled — all "nothing settled yet" for this 3-tone summary (§0.4)
}

function remindedLabel(iso: string, t: (k: string, o?: any) => string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return t('portfolio.rentRoll.reminder.remindedJustNow');
  if (hours < 48) return t('portfolio.rentRoll.reminder.remindedHours', { count: hours });
  return t('portfolio.rentRoll.reminder.remindedDays', { count: Math.floor(hours / 24) });
}

export const UnitDrillInOverlay: React.FC<{
  unitId: string;
  onClose: () => void;
  onNavigateToContacts?: () => void;
  /** Bumped on a write that changes dashboard-level data (a lease added here flips the unit's
   *  occupancy + fills the rent roll) so the parent can refetch the sections behind this overlay. */
  onChanged?: () => void;
}> = ({ unitId, onClose, onNavigateToContacts, onChanged }) => {
  const { t } = useTranslation('common');
  const [refreshKey, setRefreshKey] = useState(0);
  // I3 §3/AC3.6 — "view all payments" is a localized read-model extension of this SAME endpoint
  // (?all=1 lifts the 3-row cap), not a second route; flipping the url re-triggers useListFetch.
  const [showAllPayments, setShowAllPayments] = useState(false);
  const { status, data, retry } = useListFetch<DrillIn>(`/api/units/${unitId}/drill-in${showAllPayments ? '?all=1' : ''}`, { refreshKey });
  const feedback = useWriteFeedback();
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  // I4/R1 — "+ New work order": a fresh TicketDetailPanel create, prefilled with this unit, no
  // conversation at all (AC4.1). Set only once the unit's property-owner companyId has resolved —
  // never opened mid-fetch, so the create form never renders with a half-known context.
  const [newTicketPrefill, setNewTicketPrefill] = useState<{
    conversationId: null; companyId: string | null; leadId: string | null; unitId: string; unitCandidates: TicketUnitCandidate[];
  } | null>(null);
  const [startingTicket, setStartingTicket] = useState(false);
  // I3 §3/AC3.5 — a clicked payment row stacks the real InvoiceDetailPanel, identical idiom to the
  // work-order row's TicketDetailPanel stack below.
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  // I3 §2/AC3.1/AC3.3 — same route + one-tap + absent-not-disabled rule as RentRollCard's own
  // doMarkPaid, scoped here to the current/latest unpaid invoice row only.
  const [markPaidBusyId, setMarkPaidBusyId] = useState<string | null>(null);
  const [addingLease, setAddingLease] = useState(false);
  // I2 §2/§3 — the four lease-lifecycle actions, same state shape as InvoiceDetailPanel's own
  // confirmingVoid/editingField/fieldDraft machine (ConfirmDialog + inline click-to-edit), extended
  // to a Lease rather than invented fresh.
  const [confirmingNotice, setConfirmingNotice] = useState(false);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [leaseBusy, setLeaseBusy] = useState(false);
  const [editingLeaseField, setEditingLeaseField] = useState<'rent' | 'endDate' | null>(null);
  const [leaseFieldDraft, setLeaseFieldDraft] = useState('');
  const [renewing, setRenewing] = useState(false);
  // I5/§3/§4/AC5.2/AC5.3 — the unit's own label edit + delete, same inline click-to-edit +
  // pre-offer-refusal shape as everything else this overlay already reuses (I2's lease actions).
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [confirmingUnitDelete, setConfirmingUnitDelete] = useState(false);
  const [unitDeleteBusy, setUnitDeleteBusy] = useState(false);
  // I6/§3 — the Documents-per-lease upload: same "refetch this overlay" contract as every other write
  // here, scoped to the currently-selected lease (never offered when there is none to scope it to).
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileInputRef = useRef<HTMLInputElement>(null);

  // §0.4: Escape / backdrop click dismiss — the same technique SheetShell already uses, not a new
  // pattern. Only active while no stacked ticket overlay is open (that overlay owns Escape while up).
  useEffect(() => {
    if (openTicketId || openInvoiceId || newTicketPrefill) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, openTicketId, openInvoiceId, newTicketPrefill]);

  const doSend = async () => {
    setConfirmSend(false);
    if (!data?.reminder?.invoiceId) return;
    setSending(true);
    try {
      const res = await fetch('/api/properties/rent-roll/remind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: data.reminder.invoiceId }),
      });
      const json = await res.json().catch(() => ({}));
      const outcome: string = json?.status ?? (res.ok ? 'sent' : 'error');
      if (outcome === 'sent' || outcome === 'refused') setRefreshKey((k) => k + 1);
      else if (outcome === 'already_paid') { feedback.fail(t('portfolio.rentRoll.reminder.alreadyPaid')); setRefreshKey((k) => k + 1); }
      else if (outcome === 'no_phone') feedback.fail(t('portfolio.rentRoll.reminder.noPhone'));
      else feedback.fail(t('portfolio.rentRoll.reminder.failed'));
    } catch {
      feedback.fail(t('portfolio.rentRoll.reminder.failed'));
    } finally {
      setSending(false);
    }
  };

  // I3 §2/AC3.1/AC3.3/AC3.4 — same route + one-tap (no confirm) as RentRollCard's doMarkPaid; a 409
  // (independently voided/already paid between render and tap) refetches silently, never a generic
  // error toast. onChanged() too — paid flips figures behind this overlay (rent roll row, KPIs).
  const doMarkPaid = async (invoiceId: string) => {
    setMarkPaidBusyId(invoiceId);
    try {
      const res = await fetch(`/api/invoices/${invoiceId}/mark-paid`, { method: 'POST' });
      if (res.ok || res.status === 409) { setRefreshKey((k) => k + 1); onChanged?.(); return; }
      feedback.fail(t('portfolio.rentRoll.markPaid.failed'));
    } catch {
      feedback.fail(t('portfolio.rentRoll.markPaid.failed'));
    } finally {
      setMarkPaidBusyId(null);
    }
  };

  // I2 §2 — give-notice / end-lease / edit all share this shape: fire the verb, then refetch THIS
  // overlay (setRefreshKey, same contract as the reminder send above) AND tell the parent
  // (onChanged) since each one changes the unit's occupancy or rent-roll figures behind the overlay
  // (AC0.2 — no separate "mark unit" write exists or is needed; the next read is the truth).
  const runLeaseVerb = async (write: () => Promise<Response>, failMsg: string) => {
    setLeaseBusy(true);
    try {
      const res = await write().catch(() => null);
      if (!res?.ok) { feedback.fail(failMsg); return false; }
      setRefreshKey((k) => k + 1);
      onChanged?.();
      return true;
    } finally {
      setLeaseBusy(false);
    }
  };

  const giveNotice = async () => {
    setConfirmingNotice(false);
    if (!data?.lease) return;
    await runLeaseVerb(() => fetch(`/api/leases/${data.lease!.id}/give-notice`, { method: 'POST' }), t('writeStates.actionFailed'));
  };

  const endLease = async () => {
    setConfirmingEnd(false);
    if (!data?.lease) return;
    await runLeaseVerb(() => fetch(`/api/leases/${data.lease!.id}/end`, { method: 'POST' }), t('writeStates.actionFailed'));
  };

  const startLeaseEdit = (field: 'rent' | 'endDate', current: string) => { setLeaseFieldDraft(current); setEditingLeaseField(field); };
  const saveLeaseEdit = () => {
    if (!editingLeaseField || !data?.lease) return;
    const field = editingLeaseField;
    const leaseId = data.lease.id;
    setEditingLeaseField(null);
    let patchBody: Record<string, unknown> | null = null;
    if (field === 'rent') {
      const v = Number(leaseFieldDraft);
      if (Number.isNaN(v) || v === data.lease.rent) return;
      patchBody = { rent: v };
    } else {
      if (!leaseFieldDraft || leaseFieldDraft === data.lease.endDate.slice(0, 10)) return;
      patchBody = { endDate: leaseFieldDraft };
    }
    void runLeaseVerb(
      () => fetch(`/api/leases/${leaseId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patchBody) }),
      t('writeStates.saveFailed'),
    ).then((ok) => { if (!ok) setEditingLeaseField(field); }); // draft survives a failed save, same as InvoiceDetailPanel's reopenOnFail
  };

  // I5/§3/AC5.2 — the unit's own label, clickable on this overlay's header exactly like the lease
  // rent/endDate fields above (same pattern, extended to the Unit — "always editable here, a Unit
  // has no 'ended' state the way a Lease does, so no absent-condition applies", §3).
  const startLabelEdit = () => { if (!data) return; setLabelDraft(data.unit.label); setEditingLabel(true); };
  const saveLabelEdit = () => {
    if (!data) return;
    setEditingLabel(false);
    const draft = labelDraft.trim();
    if (!draft || draft === data.unit.label) return;
    void runLeaseVerb(
      () => fetch(`/api/units/${unitId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: draft }) }),
      t('writeStates.saveFailed'),
    ).then((ok) => { if (!ok) setEditingLabel(true); });
  };

  // I5/§4/AC5.3 — delete, same two-layer shape as AccountDetailPanel/UnitsGridCard: the pre-offer
  // check (leaseCount is already loaded) picks blocked vs destructive before the confirm renders; a
  // 409 on attempt (a Lease added in the gap between render and click) is the backstop.
  const deleteUnit = async () => {
    setConfirmingUnitDelete(false);
    setUnitDeleteBusy(true);
    try {
      const res = await fetch(`/api/units/${unitId}`, { method: 'DELETE' }).catch(() => null);
      if (res?.status === 409) { feedback.fail(t('writeStates.actionFailed')); setRefreshKey((k) => k + 1); return; }
      if (!res?.ok) { feedback.fail(t('portfolio.drillIn.deleteFailed')); return; }
      onChanged?.();
      onClose();
    } finally {
      setUnitDeleteBusy(false);
    }
  };

  // I2 §2/§3 — a lease is "ended" (settled) the moment either its explicit endedAt is set OR its own
  // endDate has simply passed — the EXACT same test the drill-in read-model used server-side to pick
  // this lease over the active one in the first place (PropertyService.ts getUnitDrillIn), so the UI
  // can never disagree with the server about which state it's in.
  const isLeaseEnded = (lease: NonNullable<DrillIn['lease']>) =>
    lease.endedAt !== null || new Date(lease.endDate).getTime() < Date.now();
  const isLeaseExpiringSoon = (lease: NonNullable<DrillIn['lease']>) =>
    !isLeaseEnded(lease) && new Date(lease.endDate).getTime() - Date.now() <= EXPIRING_SOON_WINDOW_MS;

  // I4/R1/AC4.1/AC4.2 — the unit-scoped create entry. companyId = this unit's property's owner
  // (Unit → Property → companyId, fetched fresh rather than trusted from a prop this overlay was
  // never handed); leadId = the current tenant ONLY if the drill-in's own selected lease is the
  // ACTIVE one (the exact same `isLeaseEnded` test already driving this file's lease actions above) —
  // a vacant unit, or one whose only lease on file has ended, never auto-guesses a former tenant.
  const startNewWorkOrder = async () => {
    if (!data) return;
    setStartingTicket(true);
    try {
      const res = await fetch(`/api/properties/${data.unit.propertyId}`).catch(() => null);
      if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
      const property = await res.json();
      const activeLeadId = data.lease && !isLeaseEnded(data.lease) ? data.tenant?.leadId ?? null : null;
      setNewTicketPrefill({
        conversationId: null,
        companyId: property?.companyId ?? null,
        leadId: activeLeadId,
        unitId,
        unitCandidates: [{ id: unitId, label: data.unit.label, propertyName: data.unit.propertyName }],
      });
    } finally {
      setStartingTicket(false);
    }
  };

  // I6/§3 — upload scoped to leaseId=THIS lease, reusing the existing /api/documents route with the
  // one new field (multipart, same shape as DocumentsBoard's own folderId-scoped upload).
  const uploadDocument = async (file: File) => {
    if (!data?.lease) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('leaseId', data.lease.id);
      const res = await fetch('/api/documents', { method: 'POST', body: fd }).catch(() => null);
      if (!res?.ok) { feedback.fail(t('documentsBoard.uploadFailed')); return; }
      setRefreshKey((k) => k + 1);
    } finally {
      setUploadingDoc(false);
    }
  };
  const onDocFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void uploadDocument(file);
  };

  const renderReminder = () => {
    const r = data?.reminder;
    if (!r || r.status === 'unbilled') return null; // nothing to remind about — no control, not disabled
    const trace = r.remindedAt ? (
      <div className="text-[11px] text-text-muted mt-1" data-testid="unit-drill-in-reminded">{remindedLabel(r.remindedAt, t)}</div>
    ) : null;
    if (r.status === 'paid') {
      // §0.4 explicit: disabled + title when already paid (the one case this overlay disables rather
      // than omits — Part 1's rent roll omits instead, but the row there never shows a Paid invoice's
      // action at all; here the SAME chip must stay visible so the section isn't missing its action).
      return (
        <div className="mt-2">
          <ActionChip label={t('portfolio.rentRoll.reminder.send')} onAct={() => {}} disabled
            title={t('portfolio.rentRoll.reminder.alreadyPaid')} testId="unit-drill-in-reminder-disabled" />
        </div>
      );
    }
    if (r.reachability === 'sendable') {
      return (
        <div className="mt-2">
          <ActionChip label={t('portfolio.rentRoll.reminder.send')} onAct={() => setConfirmSend(true)}
            disabled={sending} testId="unit-drill-in-reminder-send" />
          {trace}
        </div>
      );
    }
    if (r.reachability === 'window-closed') {
      return (
        <div className="mt-2">
          <SystemChip label={t('portfolio.rentRoll.reminder.closed')} tone="muted" testId="unit-drill-in-reminder-closed" />
          {trace}
        </div>
      );
    }
    // not-connected — the page-level banner (PortfolioTab) already discloses this account-wide fact.
    return trace;
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50"
        data-testid="unit-drill-in-overlay"
        onClick={onClose}
      >
        <div
          className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <ListStateShell status={status} testIdBase="unit-drill-in" onRetry={retry}>
            {data && (
              <div className="flex flex-col gap-4 p-4">
                {/* 1 — header: unit number (mono, I5/§3 editable) + status chip + close */}
                <div className="flex items-center gap-2">
                  {editingLabel ? (
                    <input autoFocus data-testid="unit-drill-in-label-input" value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)} onBlur={saveLabelEdit}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingLabel(false); }}
                      className="h-9 px-2 -mx-2 rounded-lg border border-primary text-lg font-bold font-mono w-24" dir="ltr" />
                  ) : (
                    <h2 className="text-lg font-bold text-text-strong font-mono cursor-text" data-testid="unit-drill-in-label" onClick={startLabelEdit}>{data.unit.label}</h2>
                  )}
                  <SystemChip label={t(`portfolio.unitsGrid.status.${data.unit.status}`)} token={STATUS_TOKEN[data.unit.status]} />
                  <button type="button" data-testid="unit-drill-in-close" aria-label={t('portfolio.drillIn.close')}
                    onClick={onClose} className="ms-auto min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-text-strong">
                    &times;
                  </button>
                </div>
                <div className="text-xs text-text-muted">{data.unit.propertyName}</div>

                {/* 2 — tenant: name + link out to the existing Contact detail (never duplicated here) */}
                <div className="rounded-xl border border-border-neutral bg-surface p-4">
                  <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('portfolio.drillIn.tenant.title')}</div>
                  {data.tenant ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-text-strong" data-testid="unit-drill-in-tenant-name">{data.tenant.name ?? '—'}</span>
                      {onNavigateToContacts && (
                        <button type="button" data-testid="unit-drill-in-view-contact"
                          onClick={() => { onClose(); onNavigateToContacts(); }}
                          className="text-xs font-semibold text-primary underline">
                          {t('portfolio.drillIn.tenant.viewContact')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-text-muted italic" data-testid="unit-drill-in-tenant-empty">{t('portfolio.drillIn.tenant.none')}</span>
                  )}
                </div>

                {/* 3 — lease: term, rent, renewal chip, I2's give-notice/end/edit/renew actions.
                    "Settled → absent, not disabled" (base PRD D-I2, re-applied): once endedAt is set
                    (or the lease's own endDate has simply passed), give-notice/end/edit disappear —
                    only Renew remains, same rule Send-renewal already follows for renewalState. */}
                <div className="rounded-xl border border-border-neutral bg-surface p-4">
                  <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('portfolio.drillIn.lease.title')}</div>
                  {data.lease && renewing ? (
                    // I2 §3 — Renew reuses this verbatim: a NEW Lease row via the same createLease/
                    // POST /api/leases, prefilled from the lease being renewed. startDate defaults to
                    // its endDate (contiguity, cmdr-ratified) — never "today" — and stays editable.
                    <AddLeaseForm
                      unitId={unitId}
                      initialTenant={data.tenant}
                      initialRent={data.lease.rent}
                      initialStartDate={data.lease.endDate.slice(0, 10)}
                      title={t('portfolio.drillIn.lease.renewTitle')}
                      submitLabel={t('portfolio.drillIn.lease.renewSubmit')}
                      onCreated={() => { setRenewing(false); setRefreshKey((k) => k + 1); onChanged?.(); }}
                      onCancel={() => setRenewing(false)}
                    />
                  ) : data.lease ? (
                    <div className="flex flex-col gap-1.5" data-testid="unit-drill-in-lease">
                      <div className="text-sm text-text-strong" dir="ltr">{t('portfolio.drillIn.lease.term', { start: fmtDate(data.lease.startDate), end: fmtDate(data.lease.endDate) })}</div>

                      {editingLeaseField === 'rent' ? (
                        <input autoFocus type="number" data-testid="unit-drill-in-lease-rent-input" value={leaseFieldDraft}
                          onChange={(e) => setLeaseFieldDraft(e.target.value)} onBlur={saveLeaseEdit}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingLeaseField(null); }}
                          className="h-9 px-2 rounded-lg border border-primary text-sm w-fit" dir="ltr" />
                      ) : (
                        <div className={`text-sm text-text-strong ${!isLeaseEnded(data.lease) ? 'cursor-text' : ''}`} dir="ltr"
                          onClick={!isLeaseEnded(data.lease) ? () => { if (!leaseBusy) startLeaseEdit('rent', String(data.lease!.rent)); } : undefined}
                          data-testid="unit-drill-in-lease-rent">
                          {t('portfolio.drillIn.lease.rent')}: {fmtMoney(data.lease.rent)}
                        </div>
                      )}

                      {!isLeaseEnded(data.lease) && (
                        editingLeaseField === 'endDate' ? (
                          <input autoFocus type="date" data-testid="unit-drill-in-lease-enddate-input" value={leaseFieldDraft}
                            onChange={(e) => setLeaseFieldDraft(e.target.value)} onBlur={saveLeaseEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingLeaseField(null); }}
                            className="h-9 px-2 rounded-lg border border-primary text-sm w-fit" dir="ltr" />
                        ) : (
                          <div className="text-sm text-text-strong cursor-text" dir="ltr"
                            onClick={() => { if (!leaseBusy) startLeaseEdit('endDate', data.lease!.endDate.slice(0, 10)); }}
                            data-testid="unit-drill-in-lease-enddate">
                            {t('portfolio.drillIn.lease.endDate')}: {fmtDate(data.lease.endDate)}
                          </div>
                        )
                      )}

                      {isLeaseEnded(data.lease) ? (
                        <SystemChip label={t('portfolio.drillIn.lease.endedBadge')} tone="muted" testId="unit-drill-in-lease-ended-badge" />
                      ) : (
                        <SystemChip
                          label={t(`portfolio.drillIn.lease.renewal.${data.lease.renewalState}`, data.lease.renewalState)}
                          token={data.lease.renewalState === 'renewal sent' ? 'emerald' : undefined}
                          tone="muted"
                        />
                      )}

                      <div className="flex items-center gap-2 flex-wrap pt-1">
                        {!isLeaseEnded(data.lease) && !data.lease.noticeGivenAt && (
                          <ActionChip testId="unit-drill-in-lease-give-notice" label={t('portfolio.drillIn.lease.actions.giveNotice')}
                            onAct={() => setConfirmingNotice(true)} disabled={leaseBusy} />
                        )}
                        {!isLeaseEnded(data.lease) && (
                          <ActionChip testId="unit-drill-in-lease-end" label={t('portfolio.drillIn.lease.actions.end')}
                            onAct={() => setConfirmingEnd(true)} disabled={leaseBusy} />
                        )}
                        {(isLeaseEnded(data.lease) || isLeaseExpiringSoon(data.lease)) && (
                          <ActionChip testId="unit-drill-in-lease-renew" label={t('portfolio.drillIn.lease.actions.renew')}
                            onAct={() => setRenewing(true)} disabled={leaseBusy} />
                        )}
                      </div>
                    </div>
                  ) : addingLease ? (
                    // Brief §2 — assign a lease to this vacant unit. On success: refetch THIS overlay
                    // (tenant/lease fill, status flips occupied) AND notify the parent so the units
                    // grid + rent roll behind the overlay refetch too.
                    <AddLeaseForm
                      unitId={unitId}
                      onCreated={() => { setAddingLease(false); setRefreshKey((k) => k + 1); onChanged?.(); }}
                      onCancel={() => setAddingLease(false)}
                    />
                  ) : (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-text-muted italic" data-testid="unit-drill-in-lease-empty">{t('portfolio.drillIn.lease.none')}</span>
                      <button type="button" data-testid="unit-drill-in-add-lease" onClick={() => setAddingLease(true)}
                        className="min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5 self-start">
                        + {t('portfolio.setup.addLease.button')}
                      </button>
                    </div>
                  )}
                </div>

                {/* 4 — payments: last 3 rows (or all, once "view all" is tapped — AC3.6) + the
                    already-existing "Send WhatsApp reminder" action. Each row opens the real
                    InvoiceDetailPanel stacked (AC3.5); only the current/latest UNPAID row (matches
                    reminder.invoiceId, not yet paid) also offers Mark paid (AC3.3) — a historical,
                    already-settled row never shows it. */}
                <div className="rounded-xl border border-border-neutral bg-surface p-4">
                  <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('portfolio.drillIn.payments.title')}</div>
                  {data.payments.length > 0 ? (
                    <div className="flex flex-col gap-1.5" data-testid="unit-drill-in-payments">
                      {data.payments.map((p) => {
                        const tone = paymentTone(p.status);
                        const isCurrentUnpaid = data.reminder?.invoiceId === p.id && p.status !== 'paid';
                        return (
                          <div key={p.id} className="flex items-center gap-2">
                            <button type="button" data-testid="unit-drill-in-payment-row" onClick={() => setOpenInvoiceId(p.id)}
                              className="flex-1 flex items-center justify-between gap-2 text-sm min-h-[44px] text-start hover:bg-canvas rounded-lg px-2 -mx-2">
                              <span className="text-text-muted" dir="ltr">{p.dueDate ? fmtDate(p.dueDate) : '—'}</span>
                              <span className="text-text-strong" dir="ltr">{fmtMoney(p.amount)}</span>
                              <SystemChip label={t(`portfolio.drillIn.payments.status.${tone}`)} token={PAYMENT_TOKEN[tone]} />
                            </button>
                            {isCurrentUnpaid && (
                              <ActionChip
                                label={t('invoicesGrid.markPaid')}
                                onAct={() => void doMarkPaid(p.id)}
                                disabled={markPaidBusyId === p.id}
                                testId="unit-drill-in-payment-mark-paid"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-text-muted italic" data-testid="unit-drill-in-payments-empty">{t('portfolio.drillIn.payments.none')}</span>
                  )}
                  {data.paymentsTotal > 3 && !showAllPayments && (
                    <button type="button" data-testid="unit-drill-in-payments-view-all" onClick={() => setShowAllPayments(true)}
                      className="mt-2 text-sm font-semibold text-primary">
                      {t('portfolio.drillIn.payments.viewAll')}
                    </button>
                  )}
                  {renderReminder()}
                </div>

                {/* 5 — work orders: open Tickets for this unit, each opens the real TicketDetailPanel */}
                <div className="rounded-xl border border-border-neutral bg-surface p-4">
                  <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('portfolio.drillIn.workOrders.title')}</div>
                  {data.workOrders.length > 0 ? (
                    <div className="flex flex-col gap-1.5" data-testid="unit-drill-in-work-orders">
                      {data.workOrders.map((wo) => (
                        <button key={wo.id} type="button" data-testid="unit-drill-in-work-order-row"
                          onClick={() => setOpenTicketId(wo.id)}
                          className="min-h-[44px] flex items-center justify-between gap-2 text-sm text-start hover:bg-canvas rounded-lg px-2 -mx-2">
                          <span className="text-text-strong truncate">{wo.subject}</span>
                          <SystemChip label={wo.resolvedAt ? t('ticketsGrid.status.resolved') : t('ticketsGrid.status.open')} tone="muted" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-text-muted italic" data-testid="unit-drill-in-work-orders-empty">{t('portfolio.drillIn.workOrders.none')}</span>
                  )}
                  <button type="button" data-testid="unit-drill-in-add-work-order" onClick={() => void startNewWorkOrder()} disabled={startingTicket}
                    className="mt-2 min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50">
                    + {t('portfolio.drillIn.workOrders.addButton')}
                  </button>
                </div>

                {/* 6 — documents: I6/§3, same read-model-list pattern as Payments/Work orders, scoped
                    to the currently-selected lease. Each row opens the existing Document viewer
                    (download) — reuse, not rebuild. Upload is only offered when there's a lease to
                    scope it to (never offered against "no lease", omit-not-disable). */}
                <div className="rounded-xl border border-border-neutral bg-surface p-4">
                  <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('portfolio.drillIn.documents.title')}</div>
                  {data.documents.length > 0 ? (
                    <div className="flex flex-col gap-1.5" data-testid="unit-drill-in-documents">
                      {data.documents.map((doc) => (
                        <a key={doc.id} href={`/api/documents/${doc.id}/download`} target="_blank" rel="noreferrer"
                          data-testid="unit-drill-in-document-row"
                          className="min-h-[44px] flex items-center justify-between gap-2 text-sm text-start hover:bg-canvas rounded-lg px-2 -mx-2">
                          <span className="text-text-strong truncate">{doc.name}</span>
                          <span className="text-text-muted shrink-0" dir="ltr">{fmtDocSize(doc.size)}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-text-muted italic" data-testid="unit-drill-in-documents-empty">{t('portfolio.drillIn.documents.none')}</span>
                  )}
                  {data.lease && (
                    <>
                      <button type="button" data-testid="unit-drill-in-document-upload" onClick={() => docFileInputRef.current?.click()} disabled={uploadingDoc}
                        className="mt-2 min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50">
                        + {uploadingDoc ? t('documentsBoard.uploading') : t('portfolio.drillIn.documents.upload')}
                      </button>
                      <input ref={docFileInputRef} type="file" data-testid="unit-drill-in-document-upload-input" className="hidden" onChange={onDocFilePicked} />
                    </>
                  )}
                </div>

                {/* 7 — delete, I5/§4/AC5.3: separated, text-only, rose — same footer convention as
                    AccountDetailPanel's own delete (real counts, never a category, cascade never
                    built). leaseCount is already loaded, so the pre-offer check needs no round trip. */}
                <div className="pt-2 mt-2 border-t border-border-neutral">
                  <button type="button" data-testid="unit-drill-in-delete" onClick={() => setConfirmingUnitDelete(true)} disabled={unitDeleteBusy}
                    className="text-sm font-semibold min-h-[44px] px-1 disabled:opacity-50 text-opt-rose">
                    {t('portfolio.drillIn.delete')}
                  </button>
                </div>
              </div>
            )}
          </ListStateShell>
        </div>
      </div>

      {confirmSend && data?.lease && (
        <ConfirmDialog
          title={t('portfolio.rentRoll.reminder.confirmTitle')}
          message={t('portfolio.rentRoll.reminder.confirm', {
            tenant: data.tenant?.name ?? t('portfolio.rentRoll.vacant'),
            amount: fmtMoney(data.lease.rent),
          })}
          confirmLabel={t('portfolio.rentRoll.reminder.send')}
          onConfirm={doSend}
          onClose={() => setConfirmSend(false)}
        />
      )}
      {confirmingNotice && data?.lease && (
        <ConfirmDialog
          title={t('portfolio.drillIn.lease.giveNoticeConfirmTitle')}
          message={t('portfolio.drillIn.lease.giveNoticeConfirm', { tenant: data.tenant?.name ?? '—' })}
          confirmLabel={t('portfolio.drillIn.lease.actions.giveNotice')}
          onConfirm={() => void giveNotice()}
          onClose={() => setConfirmingNotice(false)}
        />
      )}
      {confirmingEnd && data?.lease && (
        <ConfirmDialog destructive
          title={t('portfolio.drillIn.lease.endConfirmTitle')}
          message={t('portfolio.drillIn.lease.endConfirm', { tenant: data.tenant?.name ?? '—' })}
          confirmLabel={t('portfolio.drillIn.lease.actions.end')}
          onConfirm={() => void endLease()}
          onClose={() => setConfirmingEnd(false)}
        />
      )}
      {confirmingUnitDelete && data && (
        data.leaseCount > 0 ? (
          <ConfirmDialog blocked
            message={t('portfolio.drillIn.deleteBlockedByLeases', { count: data.leaseCount })}
            onClose={() => setConfirmingUnitDelete(false)} />
        ) : (
          <ConfirmDialog destructive
            message={t('portfolio.drillIn.deleteConfirm', { label: data.unit.label })}
            onConfirm={() => void deleteUnit()}
            onClose={() => setConfirmingUnitDelete(false)} />
        )
      )}
      {feedback.node}

      {/* Stacked overlay — same end-anchored idiom, rendered AFTER (so it paints on top at the same
          z-50); closing it clears openTicketId and this overlay is all that's left, unchanged. */}
      {openTicketId && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50" data-testid="unit-drill-in-ticket-overlay">
          <div className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto">
            <TicketDetailPanel
              ticketId={openTicketId}
              sheetShape={typeof window !== 'undefined' && window.innerWidth < 768 ? 'sheet' : 'popover'}
              onClose={() => setOpenTicketId(null)}
              onCreated={() => {}}
              onUpdated={() => {}}
            />
          </div>
        </div>
      )}

      {/* I3 §3/AC3.5 — a clicked payment row stacks the real InvoiceDetailPanel, the identical
          end-anchored z-50 idiom as the work-order→ticket stack above; closing it returns here.
          A write inside it (e.g. its own Mark paid) refetches this overlay so the row behind it
          agrees. */}
      {openInvoiceId && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50" data-testid="unit-drill-in-invoice-overlay">
          <div className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto">
            <InvoiceDetailPanel
              invoiceId={openInvoiceId}
              onClose={() => setOpenInvoiceId(null)}
              onCreated={() => {}}
              onUpdated={() => setRefreshKey((k) => k + 1)}
            />
          </div>
        </div>
      )}

      {/* I4/R1 — the "+ New work order" create entry, same stacked end-anchored z-50 idiom as the two
          overlays above. A successful create refetches THIS overlay (the new ticket now belongs in
          the work-orders list above, AC4.1) and notifies the parent, same contract as every other
          write in this file. */}
      {newTicketPrefill && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50" data-testid="unit-drill-in-new-ticket-overlay">
          <div className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto">
            <TicketDetailPanel
              ticketId="new"
              sheetShape={typeof window !== 'undefined' && window.innerWidth < 768 ? 'sheet' : 'popover'}
              prefill={newTicketPrefill}
              onClose={() => setNewTicketPrefill(null)}
              onCreated={() => { setNewTicketPrefill(null); setRefreshKey((k) => k + 1); onChanged?.(); }}
              onUpdated={() => {}}
            />
          </div>
        </div>
      )}
    </>
  );
};
